// ----------------------------------------------------------------------------
// French invariant words (kept identical in plural form).
// Stripping a trailing "s" would corrupt them: "anis" → "ani", etc.
// ----------------------------------------------------------------------------
const FR_INVARIANT_WORDS = new Set([
  'anis', 'ananas', 'anchois', 'cassis', 'jus', 'pois', 'ris',
  'ours', 'souris', 'brebis', 'panais', 'lis', 'lys', 'gros', 'os',
  'noix', 'choix', 'prix', 'paix', 'voix', 'faux', 'croix', 'perdrix',
  'riz', 'nez',
]);

/**
 * Singularizes a single (already lowercased & diacritic-free) French word.
 */
function singularizeFrWord(word: string): string {
  if (word.length <= 2) return word;
  if (FR_INVARIANT_WORDS.has(word)) return word;

  if (word.endsWith('z')) return word;

  if (word.endsWith('eaux') && word.length > 4) return word.slice(0, -4) + 'eau';
  if (word.endsWith('aux') && word.length > 3) return word.slice(0, -3) + 'al';
  if (word.endsWith('eux') && word.length > 3) return word.slice(0, -1);
  if (word.endsWith('oux') && word.length > 3) return word.slice(0, -1);
  if (word.endsWith('x')) return word;

  if (word.endsWith('s')) return word.slice(0, -1);

  return word;
}

/**
 * Applies French singularization to every lexical token of a string.
 */
function singularizeFr(text: string): string {
  return text.replace(/[a-z]+/g, (word) => singularizeFrWord(word));
}

/**
 * Normalize a string for ingredient matching by:
 * - Replacing French ligatures (œ → oe, æ → ae)
 * - Removing accents/diacritics
 * - Converting to lowercase
 * - Collapsing whitespace and trimming
 * - Singularizing French plurals (carottes → carotte, poireaux → poireau)
 *
 * Applied to BOTH sides of any comparison (catalog + query) so the matching
 * stays symmetric. Examples:
 *  - "Bœuf"      → "boeuf"
 *  - "Carottes"  → "carotte"
 *  - "Poireaux"  → "poireau"
 *  - "Œufs"      → "oeuf"
 */
export function normalizeString(str: string): string {
  const stripped = str
    .replace(/œ/g, 'oe')
    .replace(/Œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/Æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  return singularizeFr(stripped);
}

